

import React from 'react';
import { motion } from 'framer-motion';
import {
    Cpu,
    Network,
    Layers,
    Rocket,
    Zap,
    ShieldCheck,
    CheckCircle2
} from 'lucide-react';

const WhyChoose = () => {
    const sections = [
        {
            icon: <Cpu size={28} />,
            title: "AI-Powered Recruitment Intelligence",
            desc: (
                <>
                    We leverage AI-supported recruitment workflows to improve sourcing efficiency, candidate filtering, hiring coordination, and recruitment scalability through modern <a href="api/blog/ai-recruitment-transforming-talent-acquisition" className="text-blue-500 underline">AI recruitment strategies and recruitment automation best practices</a>.
                </>
            ),
            bullets: ["Reduce screening delays", "Improve candidate matching", "Streamline workflows", "Accelerate hiring execution"],
            color: "#00f2ff"
        },
        {
            icon: <Network size={28} />,
            title: "Specialized Industry Hiring Teams",
            desc: "Unlike generic staffing firms, our recruiters specialize in industry-focused hiring ecosystems across high-growth sectors.",
            bullets: ["IT & Software", "BFSI", "Healthcare", "Manufacturing", "Ecommerce", "Logistics", "Startups", "GCCs"],
            color: "#00f2ff"
        },
        {
            icon: <Layers size={28} />,
            title: "End-To-End Workforce Ecosystem",
            desc: "Most agencies stop after onboarding. We support businesses across the complete workforce lifecycle.",
            bullets: ["Staffing & RPO", "Payroll Outsourcing", "Statutory Compliance", "HR Administration", "Contractor Payroll"],
            color: "#00f2ff"
        },
        {
            icon: <Rocket size={28} />,
            title: "Built For High-Growth Business Environments",
            desc: "Our systems are designed for organizations experiencing aggressive hiring demand and operational expansion.",
            bullets: ["Aggressive hiring demand", "Rapid scaling", "GCC setup", "Startup team building"],
            color: "#00f2ff"
        },
        {
            icon: <Zap size={28} />,
            title: "Faster Hiring Without Recruitment Chaos",
            desc: "Unstructured hiring creates delays. Our streamlined execution systems help businesses manage bulk hiring efficiently.",
            bullets: ["Reduce turnaround time", "Improve visibility", "Manage bulk hiring", "Smooth candidate experiences"],
            color: "#00f2ff"
        },
        {
            icon: <ShieldCheck size={28} />,
            title: "Workforce Compliance & Payroll Confidence",
            desc: "Payroll errors create financial risks. Our systems help businesses maintain accurate processing and documentation consistency.",
            bullets: ["Accurate payroll", "Statutory compliance", "PF & ESI management", "Documentation consistency"],
            color: "#00f2ff"
        }
    ];

    return (
        <section className="py-10 relative overflow-hidden" style={{ backgroundColor: '#01014C' }}>
            {/* Background Ambient Glows */}
            <div className="absolute top-0 left-0 w-96 h-96 bg-blue-600/10 blur-[120px] rounded-full" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-600/10 blur-[120px] rounded-full" />

            <div className="container mx-auto px-6 relative z-10">
                <header className="mb-32 text-center">
                    <motion.p
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        className="text-cyan-400 font-mono tracking-[0.4em] uppercase text-xs mb-4"
                    >
                        Merida vs Traditional
                    </motion.p>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        className="text-white text-3xl md:text-5xl font-bold syne leading-tight"
                    >
                        The Strategic <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">Difference</span>
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        className="text-slate-300 text-sm md:text-lg max-w-2xl mx-auto leading-relaxed mt-4 font-medium"
                    >
                        Unlike traditional staffing agencies, we provide integrated HR strategies that align candidate acquisition with business performance outcomes and operational scalability.
                    </motion.p>
                </header>

                <div className="relative">
                    {/* CENTRAL LINE */}
                    <div className="absolute left-1/2 top-0 bottom-0 w-[1px] bg-gradient-to-b from-transparent via-white/20 to-transparent hidden lg:block -translate-x-1/2" />

                    <div className="space-y-14 lg:space-y-5">
                        {sections.map((item, index) => {
                            const isEven = index % 2 === 0;

                            return (
                                <div key={index} className="relative flex flex-col lg:flex-row items-center justify-between w-full">

                                    {/* LEFT SIDE (Content if Even, Empty if Odd) */}
                                    <div className="w-full lg:w-[45%]">
                                        {isEven && (
                                            <motion.div
                                                initial={{ opacity: 0, x: -100 }}
                                                whileInView={{ opacity: 1, x: 0 }}
                                                viewport={{ once: true, amount: 0.5 }}
                                                transition={{ duration: 0.8, ease: "easeOut" }}
                                                className="text-right flex flex-col items-end"
                                            >
                                                <div className="flex items-center gap-4 mb-4 justify-end">
                                                    <h3 className="text-white text-2xl md:text-xl font-semibold syne text-right">{item.title}</h3>
                                                    <div className="p-2 rounded-2xl bg-white/5 border border-white/10 text-white shrink-0" style={{ boxShadow: `0 0 20px ${item.color}22` }}>
                                                        {item.icon}
                                                    </div>
                                                </div>
                                                <p className="text-white text-sm md:text-base leading-relaxed mb-6">{item.desc}</p>
                                                <div className="flex flex-wrap justify-end gap-x-6 gap-y-3">
                                                    {item.bullets?.map((b, i) => (
                                                        <span key={i} className="flex items-center gap-2 text-xs text-white/80 font-medium">
                                                            {b} <CheckCircle2 size={14} style={{ color: item.color }} />
                                                        </span>
                                                    ))}
                                                </div>
                                            </motion.div>
                                        )}
                                    </div>

                                    {/* CENTER DOT */}
                                    <div className="absolute left-1/2 -translate-x-1/2 hidden lg:flex items-center justify-center z-20">
                                        <motion.div
                                            initial={{ scale: 0 }}
                                            whileInView={{ scale: 1 }}
                                            className="w-4 h-4 rounded-full border-4 border-[#01014C]"
                                            style={{ backgroundColor: item.color, boxShadow: `0 0 15px ${item.color}` }}
                                        />
                                    </div>

                                    {/* RIGHT SIDE (Empty if Even, Content if Odd) */}
                                    <div className="w-full lg:w-[45%] mt-12 lg:mt-0">
                                        {!isEven && (
                                            <motion.div
                                                initial={{ opacity: 0, x: 100 }}
                                                whileInView={{ opacity: 1, x: 0 }}
                                                viewport={{ once: true, amount: 0.5 }}
                                                transition={{ duration: 0.8, ease: "easeOut" }}
                                                className="text-left flex flex-col items-start"
                                            >
                                                <div className="flex items-center gap-4 mb-4 justify-start">
                                                    <div className="p-2 rounded-2xl bg-white/5 border border-white/10 text-white shrink-0" style={{ boxShadow: `0 0 20px ${item.color}22` }}>
                                                        {item.icon}
                                                    </div>
                                                    <h3 className="text-white text-2xl md:text-xl font-semibold syne text-left">{item.title}</h3>
                                                </div>
                                                <p className="text-white text-sm md:text-base leading-relaxed mb-6">{item.desc}</p>

                                                {item.bullets && (
                                                    <div className="flex flex-wrap justify-start gap-x-6 gap-y-3">
                                                        {item.bullets.map((b, i) => (
                                                            <span key={i} className="flex items-center gap-2 text-xs text-white/80 font-medium">
                                                                <CheckCircle2 size={14} style={{ color: item.color }} /> {b}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}

                                                {item.grid && (
                                                    <div className="flex flex-wrap gap-2">
                                                        {item.grid.map((chip, i) => (
                                                            <span key={i} className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-[10px] text-white/60 font-bold uppercase tracking-wider">
                                                                {chip}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </motion.div>
                                        )}
                                    </div>

                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default WhyChoose;